import { amber, red, blue, teal } from '@material-ui/core/colors';
import { Grid, Paper, Tooltip, Typography } from '@material-ui/core';
import { useCompanyMaster, useSnackbarInstance } from '@lib/common';
import { useCallback, useMemo, useState, useRef } from 'react';
import { scrollToElement, withScrollSpyItem } from '@components/scroll-spy';
import { Column } from 'react-table';
import { DraggableRowsTable } from '@components/draggable-rows-table';
import { ColoredChipIconButton } from '@components/colored-elements';
import { ImDrawer2, TrashFill, BiEdit } from '@icon/react-icons';
import { createContextQuick } from '@components/quick-context';
import { Control, useController, useFormContext } from 'react-hook-form';
// import { TypesContainer } from '../company-types';
// import { createFormField } from '../../common';
import getLoadingMessage from '@lib/common/loading-messages';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import { ContentMedia } from '@lib/content';
import styled from 'styled-components';
import { thumbStack } from '@lib/common/css';
import { ColoredAvatar, ColoredChip } from '@components/colored-elements';
import { MediaPicker } from './MediaPicker';

export const MediaLoader = ({ value = null, onChange = null }) => {
  const { enqueue } = useSnackbarInstance();

  const onMediaAdd = useCallback(
    (media: ContentMedia) => {
      if (value && onChange) {
        if (!value.find(v => v.path === media.path && v.video_site === media.video_site)) {
          onChange([...value, media]);
        } else {
          enqueue(
            <>
              Media &nbsp;<strong>{`${media.video_site} with path ${media.path}`}</strong>&nbsp; is
              already added.
            </>,
            { variant: 'error' }
          );
        }
      } else if (!value) {
        // Adding new media and the value is not usable.
        
        onChange([media]);
      }
    },
    [value, onChange]
  );

  const onMediaDelete = (media: ContentMedia) => {
    if (value && onChange) {
      const idx = value.findIndex(v => v.path === media.path && v.video_site === media.video_site);
      if (idx >= 0) {
        const nextValue = [...value];
        nextValue.splice(idx, 1);
        onChange(nextValue);
      }
    }
  };

  const onEditMedia = (media: ContentMedia, idx?: number) => {
    if (value && onChange) {
      if (idx >= 0) {
        const nextValue = [...value];
        nextValue[idx] = media;
        onChange(nextValue);
      }
    }
  };

  const MediaColumn: Column<ContentMedia>[] = useMemo(
    () => [
      {
        Header: '#',
        id: 'serial',
        accessor: (_row, idx) => idx + 1,
        width: 35,
      },
      {
        Header: 'Video Name',
        id: 'video_name',
        accessor: row => row.video_name,
        width: 200,
      },
      {
        Header: 'Video Site',
        id: 'video_site',
        accessor: row => row.video_site,
        width: 200,
      },
      {
        Header: 'Path',
        id: 'path',
        accessor: row => row.path,
        width: 200,
      },
      {
        Header: 'Video Type',
        id: 'video_type',
        accessor: row => row.video_type,
        width: 200,
      },
      {
        Header: 'Video Language',
        id: 'language_id',
        accessor: row => row.language_id.name,
        width: 200,
      },
      {
        Header: 'Action',
        id: 'actions',
        width: 80,
        Cell({ row }) {
          const [eachMediaEdit, setEachMediaEdit] = useState(false);
          const { deleteMedia, editMedia } = useActions();
          const onDeleteClick = () => deleteMedia(row.original);
          const onEditClick = () => {
            setEachMediaEdit(true);
          };
          const editMediaFunction = (media, idx) => {
            editMedia(media, idx);
            setEachMediaEdit(false);
          };

          return (
            <>
              <Tooltip title="Edit">
                <span style={{ paddingRight: '5px' }}>
                  <ColoredChipIconButton
                    onClick={onEditClick}
                    pointer
                    colored={blue[900]}
                    size="small"
                    disabled={false}
                    icon={<BiEdit />}
                  />

                  <MediaPicker
                    onMediaAdd={onMediaAdd}
                    onMediaEdit={editMediaFunction}
                    MediaEditState={eachMediaEdit}
                    editMediaScenario={true}
                    editMediaIndex={row.index}
                    editMediaData={row.original}
                    setEachMediaEdit={setEachMediaEdit}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <span>
                  <ColoredChipIconButton
                    onClick={onDeleteClick}
                    pointer
                    colored={red[900]}
                    size="small"
                    icon={<TrashFill />}
                  />
                </span>
              </Tooltip>
            </>
          );
        },
      },
    ],
    []
  );
  return (
    <MediaContainer>
      <Grid container spacing={4} justify="center" alignItems="center">
        <Grid item>
          <Typography variant="h6" style={{ fontSize: 16 }}>
            Manage Content Media
          </Typography>
        </Grid>
        <Grid item xs />
        <Grid item>
          <MediaPicker onMediaAdd={onMediaAdd} />
        </Grid>
      </Grid>
      {value && value.length ? (
        <Paper className="fixed-height">
          <ProvideEdit deleteMedia={onMediaDelete} editMedia={onEditMedia}>
            <DraggableRowsTable
              getRowId={getMediaRowId}
              onChange={onChange}
              columns={MediaColumn}
              data={value}
              stickyHeader
              size="small"
              showDragIcon={false}
              // stopDrag={true}
            />
          </ProvideEdit>
        </Paper>
      ) : (
        EmptyContent
      )}
    </MediaContainer>
  );
};

const EmptyContent = (
  <Paper className="fixed-height no-data">
    <span>
      <ImDrawer2 />
      <br />
      <Typography>
        Click on <strong>Add</strong> button
        <br />
        and start managing Media.
      </Typography>
    </span>
  </Paper>
);

const [ProvideEdit, useActions] =
  createContextQuick<{
    deleteMedia?: (media: ContentMedia) => any;
    editMedia?: (media: ContentMedia, idx?: number) => any;
  }>();
const getMediaRowId = (row, idx) => row.path + row.video_site + idx;

const useFocusableDiv = (offset = 160) => {
  const divRef = useRef<HTMLDivElement>();
  const focus = useCallback(
    () => divRef.current && scrollToElement(divRef.current, offset),
    [divRef, offset]
  );
  return { divRef, focus };
};

interface FormFieldProps {
  name: string;
  control: Control;
  [k: string]: any;
}

const createFormField =
  (
    InnerComponent: React.ComponentType<{ value: any; onChange: any }>,
    { defaultValue = null, passProps = false }: { defaultValue?: any; passProps?: boolean } = {}
  ) =>
  ({ name, control, ...otherProps }: FormFieldProps) => {
    const { divRef, focus: onFocus } = useFocusableDiv();
    const {
      field: { value, onChange },
    } = useController({ name, control, defaultValue, onFocus });
    const passingProps = passProps ? otherProps : {};
    return (
      <div ref={divRef}>
        <InnerComponent value={value} onChange={onChange} {...passingProps} />
      </div>
    );
  };
export const MediaField = createFormField(MediaLoader, {
  defaultValue: [],
  passProps: true,
});

const MediaContainer = styled.div`
  padding: 10px 5px;
  background: #f5f5f5;
  table {
    td:nth-of-type(1) {
      padding-left: 10px;
      width: 40px;
    }
    th:nth-of-type(1) {
      padding-left: 10px;
      width: 40px;
    }
    // td:nth-of-type(2) {
    //   width: 35px;
    // }
    td:last-child {
      width: 80px;
    }
  }
  .drag-icon {
    cursor: grab;
  }
  .fixed-height {
    ${thumbStack}
    margin-top: 6px;
    margin-bottom: 6px;
    height: 40vh;
    position: relative;
    overflow: auto;
    .MuiTableContainer-root {
      ${thumbStack}
      height: inherit;
      overflow: inherit;
    }
    &.no-data {
      display: flex;
      align-items: center;
      justify-content: center;
      span {
        text-align: center;
        svg {
          font-size: 4em;
          color: #ccc;
        }
      }
    }
  }
`;
